import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { AccountAction } from 'dashboard/models/account-action';
import { WorkflowStatus } from 'dashboard/models/workflow';
import { AccountActionsFilterParams } from 'dashboard/redux/actions/account-actions';
import { initialAccountActionsFilterState } from 'dashboard/redux/reducers/account-actions';
import AccountActionsTable from 'dashboard/views/DashboardAccountActionsView/AccountActionsTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: WorkflowStatus.Running,
  },
  {
    label: 'Inactive',
    value: WorkflowStatus.Paused,
  },
  {
    label: 'Archived',
    value: WorkflowStatus.Archived,
  },
];

const SearchButtonRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 300px;
  grid-gap: ${Spacing[4]};
`;

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
`;

export interface DashboardAccountActionsViewProps {
  isLoading: boolean;
  productFeatureIsDisabled: boolean;
  accountTypeId: string;
  accountActions: AccountAction[];
  sortParams: SortParams;
  filters: AccountActionsFilterParams;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleOmniSearch: (omni: string) => void;
  handleStatusFilterUpdate: (status: WorkflowStatus[]) => void;
  handleCreateAccountAction: () => void;
  handleArchiveAccountAction: (accountAction: AccountAction) => void;
  handleActivateAccountAction: (accountAction: AccountAction) => void;
  handleDeactivateAccountAction: (accountAction: AccountAction) => void;
}

const DashboardAccountActionsView = ({
  isLoading,
  accountTypeId,
  accountActions,
  filters,
  sortParams,
  handleSortUpdate,
  handleOmniSearch,
  handleStatusFilterUpdate,
  handleCreateAccountAction,
  handleArchiveAccountAction,
  handleActivateAccountAction,
  handleDeactivateAccountAction,
}: DashboardAccountActionsViewProps) => {
  const canCreateAccountAction = usePermission(Permission.AccountActionWrite);
  const { status, omni } = filters;

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
    emptyIsAll: true,
  });

  // A filter is set by default so we check for equality rather than emptiness
  const hasUsedFilters = !isEqual(filters, initialAccountActionsFilterState);

  return (
    <>
      <Card>
        <Card.Body>
          <SearchButtonRow>
            <SearchInput
              handleInput={handleOmniSearch}
              value={omni ?? ''}
              size='md'
              placeholder='Search by action name'
              id='account-actions-view__search-input'
            />
            <div>
              <FormControlLabel>Filtered by</FormControlLabel>
              <MultiSelect
                value={status ?? []}
                size='md'
                selectOptionValues={STATUS_FILTER_OPTIONS}
                labelTextMapper={statusFilterLabelTextMapper}
                onChange={handleStatusFilterUpdate}
              />
            </div>
          </SearchButtonRow>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <TableTitleContainer>
            <Card.Title>Actions</Card.Title>
            <HStack>
              <Button
                id='account-type-account-actions-table__add-action-button'
                aria-label='Create action'
                variant='basic'
                prefixIcon={<PlusIcon />}
                onClick={handleCreateAccountAction}
                disabled={!canCreateAccountAction}
              >
                Create action
              </Button>
            </HStack>
          </TableTitleContainer>
          <AccountActionsTable
            isLoading={isLoading}
            sortParams={sortParams}
            accountTypeId={accountTypeId}
            accountActions={accountActions}
            hasUsedFilters={hasUsedFilters}
            handleSortUpdate={handleSortUpdate}
            handleArchiveAccountAction={handleArchiveAccountAction}
            handleActivateAccountAction={handleActivateAccountAction}
            handleDeactivateAccountAction={handleDeactivateAccountAction}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default DashboardAccountActionsView;
